import React, {Fragment, useCallback, useEffect, useState} from 'react';
import {USER_FIELDS} from 'shared/student/registration/actions';
import Onboarding from 'src/components/Onboarding';
import UserService from 'src/services/UserService';
import {HighSchoolOnboardingForm} from 'src/views/high-school/Onboarding/types';
import {StepConfig, StepPosition} from 'src/types/form';
import {OnboardingActions} from 'src/types/onboarding';
import {EligibilityStepConfig} from 'src/components/form/onboarding/eligibility';
import {AchievementSelectionConfig} from 'src/components/form/onboarding/AchievementSelection';
import {FollowCollegesConfig} from 'src/components/form/onboarding/FollowColleges';
import {cloneDeep, noop} from 'lodash';
import {Track} from 'src/utils/analytics';
import {MobileNumberStepConfig} from 'src/components/form/onboarding/MobileNumber';
import {FormikHelpers} from 'formik';
import {getFieldErrors} from 'src/utils/formatters';
import {ApiError} from 'src/utils/errors/ApiErrorHandler';
import Layout from 'src/components/Onboarding/Layout';
import {addDefaultsToStudent} from 'src/utils/onboarding';
import NameAndDobForm from '../../public/sign-up/name-and-dob-form/name-and-dob-form';
import {VISITED_ELIGIBILITY} from '../../../components/Onboarding/utils';
import {BasicLoginError} from '../../../utils/errors/BasicLoginError';
import {TOO_YOUNG} from '../../public/utils';
import Errors from '../../../static/errors.json';

export const stepsInOrder = [
  AchievementSelectionConfig,
  FollowCollegesConfig,
  MobileNumberStepConfig,
  EligibilityStepConfig,
] as StepConfig<HighSchoolOnboardingForm, OnboardingActions>[];

export function updateFieldErrors(
  form: FormikHelpers<HighSchoolOnboardingForm>,
  error: $TSFixMe
): void {
  const loginErrors = new BasicLoginError(error);
  const fieldErrors = loginErrors.getFieldErrors();

  if (fieldErrors.birthday === Errors.validation.birthday.tooYoung) {
    window.sessionStorage.setItem(TOO_YOUNG, 'true');
  }

  if (Array.isArray(error)) {
    const formErrors = getFieldErrors(error as ApiError[]);
    form.setErrors(formErrors);
  } else {
    console.log('Unknown error response type', error);
  }
}

interface HighSchoolOnboardingProps {
  withLayout?: boolean;
}

export function HighSchoolOnboarding({withLayout}: HighSchoolOnboardingProps) {
  const [student, setStudent] = useState<HighSchoolOnboardingForm>();
  const getActions = useCallback((): OnboardingActions => ({deleteStudent: noop}), []);

  const getStudent = (): HighSchoolOnboardingForm => student as HighSchoolOnboardingForm;

  useEffect(() => {
    UserService.getData<HighSchoolOnboardingForm & {isUsCitizenOrResident: boolean | null}>(
      USER_FIELDS
    ).then((user) => setStudent(addDefaultsToStudent(user)));
  }, []);

  if (!student) return null;

  async function finishOnboarding(
    form: HighSchoolOnboardingForm,
    stepPosition: StepPosition
  ): Promise<void> {
    const updatedForm = cloneDeep(form);
    updatedForm.onboardingCompletedAt = Date.now();
    Track.onboardingStepComplete(updatedForm._id, stepPosition.key);
    Track.onboardingComplete(updatedForm._id, updatedForm.educationPhase);
    await UserService.update(updatedForm);
    window.localStorage.removeItem(VISITED_ELIGIBILITY);
    location.href = '/home';
  }

  async function updateUser(
    values: HighSchoolOnboardingForm,
    stepPosition: StepPosition,
    form: FormikHelpers<HighSchoolOnboardingForm>
  ): Promise<boolean> {
    const updatedForm = cloneDeep(values);

    try {
      let updatedStudent = await UserService.update(updatedForm, USER_FIELDS);
      updatedStudent = addDefaultsToStudent(updatedStudent);
      setStudent(updatedStudent);
      window.localStorage.removeItem(VISITED_ELIGIBILITY);
    } catch (error: $TSFixMe) {
      updateFieldErrors(form, error);

      return error;
    }

    Track.onboardingStepComplete(updatedForm._id, stepPosition.key);
    return true;
  }

  if (!student.birthday || !student.firstName || !student.lastName) {
    return (
      <NameAndDobForm<HighSchoolOnboardingForm>
        size={55}
        student={student}
        updateUser={updateUser}
      />
    );
  }

  const Wrapper = withLayout ? Layout : Fragment;

  return (
    <Wrapper>
      <Onboarding<HighSchoolOnboardingForm, OnboardingActions>
        basePath="/students/onboarding"
        getActions={getActions}
        getValues={getStudent}
        onIncrementalSubmit={updateUser}
        onSubmit={finishOnboarding}
        steps={stepsInOrder}
        hasEligibilityPage
        formikProps={{
          initialValues: student,
          enableReinitialize: true,
        }}
      />
    </Wrapper>
  );
}

export const MobileHighSchoolOnboarding = () => <HighSchoolOnboarding />;

export default function WrappedHighSchoolOnboarding() {
  return <HighSchoolOnboarding withLayout />;
}
